import { useMemo, useRef, useState } from 'react';
import { Box, Flex, Select, Text } from '@mantine/core';
import { TbChevronDown, TbClipboardText } from 'react-icons/tb';
import { useRegisterCellRef } from '../useRegisterCellRef';
import ReportCellComment from '../../../../components/ReportComments/ReportCellComment';
import { NewTableCellWrapper } from './style';
import commonStore from '../../../../../../store/common';
import { HEADER_BG } from '../tableConstants';
import { idCreator } from '../../../../../Project/utils/idCreator';

export const SelectCell = ({
  value,
  options,
  onChange,
  error = null,
  cellRefs = null,
  cellKey = null,
  onCellKeyDown = null,
  fundId = null,
  fundName = null,
  tableName = null,
  fieldName = null,
  fieldOrder = null,
  isFromStaticTemplate = false
}) => {
  const [isFocused, setIsFocused] = useState(false);
  // Mount the comment icon/popover only after the first hover (see SelectCellView
  // in NewSelectCellTemplate). Latched true so it stays mounted once shown.
  const [commentMounted, setCommentMounted] = useState(false);
  const cellRef = useRegisterCellRef({ cellRefs, cellKey });
  const selectRef = useRef(null);
  const { isViewer } = commonStore;
  const cellId = idCreator([fundId, tableName, fieldName, fieldOrder].filter(Boolean).join('-'));

  // Shift the comment icon left of any trailing icons so it doesn't overlap them.
  const commentRightOffset = useMemo(() => {
    let offset = 28;
    if (isFromStaticTemplate) offset += 28;
    return offset;
  }, [isFromStaticTemplate]);

  const handleKeyDown = e => {
    if (
      onCellKeyDown &&
      cellKey &&
      ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)
    ) {
      if (!e.target.closest('.mantine-Select-dropdown')) {
        e.preventDefault();
        onCellKeyDown(e, cellKey);
      }
    }
  };

  return (
    <NewTableCellWrapper
      ref={cellRef}
      sx={() => ({
        border: isFocused ? '1px solid #228BE6' : '1px solid transparent'
      })}
      pos="relative"
      $commentRightOffset={commentRightOffset}
      onPointerEnter={() => setCommentMounted(true)}
    >
      <Flex align="center" gap="4px" justify="space-between">
        <Select
          id={`formpf-select-cell-${cellId}`}
          ref={selectRef}
          w="100%"
          h="100%"
          styles={{
            input: {
              border: 'none',
              height: '100%',
              fontSize: '12px',
              lineHeight: '17px',
              '&:disabled': {
                backgroundColor: HEADER_BG
              }
            },
            wrapper: { height: '100%' },
            dropdown: {
              zIndex: '99999999!important'
            }
          }}
          rightSection={<TbChevronDown size={16} />}
          data={options}
          value={value}
          onChange={onChange}
          checkIconPosition="right"
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={isViewer || isFromStaticTemplate}
        />
        {isFromStaticTemplate && (
          <TbClipboardText style={{ minWidth: '12px', marginRight: '12px' }} fontSize="12px" />
        )}
      </Flex>
      {error && (
        <Box p="7.5px 12px" h="100%">
          {error && (
            <Text id={`formpf-select-cell-error-${cellId}`} fz="12px" mt="4px" c="red" lh="17px">
              {error.msg}
            </Text>
          )}
        </Box>
      )}
      <ReportCellComment
        fundId={fundId}
        fundName={fundName}
        tableName={tableName}
        fieldName={fieldName}
        fieldOrder={fieldOrder}
        cellHovered={commentMounted}
      />
    </NewTableCellWrapper>
  );
};
